import { useState } from "react";
import { Slider } from "../ui/slider";
import { DEFAULT_ADJUSTMENTS } from "../../lib/adjustments";
import { CaretDown, ArrowCounterClockwise, Sliders } from "@phosphor-icons/react";
import CurveEditor from "./CurveEditor";

function AdjRow({ label, value, min, max, step = 1, def = 0, fmt, onStart, onChange, testid }) {
  return (
    <div className="mb-3" data-testid={`adj-${testid}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-zinc-400">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] tabular-nums text-zinc-300 w-10 text-right">{fmt ? fmt(value) : value}</span>
          <button
            onClick={() => { onStart(); onChange(def); }}
            title="Reset"
            data-testid={`reset-${testid}`}
            className="h-5 w-5 rounded flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10"
          >
            <ArrowCounterClockwise size={12} />
          </button>
        </div>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onPointerDown={onStart} onValueChange={(v) => onChange(v[0])} />
    </div>
  );
}

export default function AdjustmentsPanel({ el, onUpdate, onStart }) {
  const [open, setOpen] = useState(false);
  const a = { ...DEFAULT_ADJUSTMENTS, ...(el.adjustments || {}) };
  const set = (patch) => onUpdate({ adjustments: { ...a, ...patch } });

  return (
    <div className="border-t border-white/10 pt-3 mt-3">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="advanced-adjustments-toggle"
        className="w-full flex items-center justify-between text-left mb-1"
      >
        <span className="text-xs font-semibold flex items-center gap-1.5 text-zinc-200">
          <Sliders size={15} className="text-blue-400" /> Advanced Adjustments
        </span>
        <CaretDown size={14} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3" data-testid="adjustments-panel">
          {/* Exposure & Contrast */}
          <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-2">Exposure & Contrast</p>
          <AdjRow label="Exposure" testid="exposure" value={a.exposure} min={-3} max={3} step={0.05} def={0} fmt={(v) => v.toFixed(2)} onStart={onStart} onChange={(v) => set({ exposure: v })} />
          <AdjRow label="Contrast" testid="contrast" value={a.contrast} min={-100} max={100} def={0} onStart={onStart} onChange={(v) => set({ contrast: v })} />

          {/* HSL */}
          <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-2 mt-4">Hue / Saturation / Lightness</p>
          <AdjRow label="Hue" testid="hue" value={a.hue} min={-180} max={180} def={0} fmt={(v) => `${v}°`} onStart={onStart} onChange={(v) => set({ hue: v })} />
          <AdjRow label="Saturation" testid="saturation" value={a.saturation} min={-100} max={100} def={0} onStart={onStart} onChange={(v) => set({ saturation: v })} />
          <AdjRow label="Lightness" testid="lightness" value={a.lightness} min={-100} max={100} def={0} onStart={onStart} onChange={(v) => set({ lightness: v })} />

          {/* Levels */}
          <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-2 mt-4">Levels</p>
          <AdjRow label="Black input" testid="level-black" value={a.levelBlack} min={0} max={254} def={0} onStart={onStart} onChange={(v) => set({ levelBlack: Math.min(v, a.levelWhite - 1) })} />
          <AdjRow label="Gamma" testid="gamma" value={a.gamma} min={0.1} max={9.9} step={0.1} def={1.0} fmt={(v) => v.toFixed(1)} onStart={onStart} onChange={(v) => set({ gamma: v })} />
          <AdjRow label="White input" testid="level-white" value={a.levelWhite} min={1} max={255} def={255} onStart={onStart} onChange={(v) => set({ levelWhite: Math.max(v, a.levelBlack + 1) })} />

          {/* Curves */}
          <div className="flex items-center justify-between mb-2 mt-4">
            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">Curves</p>
            <button
              onClick={() => { onStart(); set({ curve: [[0, 0], [255, 255]] }); }}
              data-testid="reset-curve"
              className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1"
            >
              <ArrowCounterClockwise size={11} /> Reset
            </button>
          </div>
          <CurveEditor points={a.curve} onStart={onStart} onChange={(curve) => set({ curve })} />
          <p className="text-[10px] text-zinc-500 mt-1.5">Click to add a point · drag to shape · double-click to remove.</p>

          <button
            onClick={() => { onStart(); onUpdate({ adjustments: { ...DEFAULT_ADJUSTMENTS } }); }}
            data-testid="reset-all-adjustments"
            className="w-full mt-4 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-sm font-medium text-zinc-200 flex items-center justify-center gap-2 transition-colors"
          >
            <ArrowCounterClockwise size={15} /> Reset all adjustments
          </button>
        </div>
      )}
    </div>
  );
}
